import {useCallback, useEffect, useMemo, useState} from 'react';
import {Box, Button, Skeleton, Stack, Typography} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import {SplitTabsPanel} from '@/components/common';
import {
    AuditProgramSectionVO,
    getQSectionVO,
    saveQSectionPage,
} from '@/services/auditService/auditProgramService';
import {
    Q0_PAGE,
    Q1_2_PAGE,
    Q1_X11_PAGE,
    Q2_X11_PAGE,
    Q_PAGE_TITLES,
    Q_SECTION_PAGES,
    QCutoffPage,
    QLeadPage,
    QProgrammePage,
    QSectionPageCode,
} from '@/pages/components/audit-program/q-section/constants';
import QCutoffView from '@/pages/components/audit-program/q-section/QCutoffView';
import JBreakdownView from '@/pages/components/audit-program/j-section/JBreakdownView';
import G0ProgrammeView from '@/pages/components/audit-program/g-section/G0ProgrammeView';
import PageTabLabel from '@/pages/components/audit-program/PageTabLabel';
import {useProgramSectionTemplates} from '@/pages/components/audit-program/templates/useProgramSectionTemplates';

export type QSectionViewProps = {
    companyId: number;
    annualId: number;
    companyName?: string | null;
    initialPage?: QSectionPageCode;
};

function toStringRecord(data?: Record<string, unknown>): Record<string, string> {
    if (!data) return {};
    return Object.fromEntries(
        Object.entries(data).map(([k, v]) => [k, v == null ? '' : String(v)]),
    );
}

function withHeaderDefaults(
    page: Record<string, string>,
    data: AuditProgramSectionVO,
    refCode: string,
): Record<string, string> {
    return {
        ...page,
        client: page.client || data.client || '',
        yearEnd: page.yearEnd || data.yearEnd || '',
        fileNo: page.fileNo || data.fileNo || '',
        preparedBy: page.preparedBy || data.preparedInitial || data.preparedBy || '',
        completedBy: page.completedBy || data.preparedInitial || data.preparedBy || '',
        reviewedBy: page.reviewedBy || data.reviewedInitial || data.reviewedBy || '',
        ref: page.ref || refCode,
    };
}

export default function QSectionView({
    companyId,
    annualId,
    companyName,
    initialPage = 'Q0',
}: QSectionViewProps) {
    const [pageKey, setPageKey] = useState<QSectionPageCode>(initialPage);
    const [header, setHeader] = useState<AuditProgramSectionVO | null>(null);
    const [pageData, setPageData] = useState<Record<string, Record<string, string>>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [dirty, setDirty] = useState<Record<string, boolean>>({});
    const {getContent} = useProgramSectionTemplates('Q');

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await getQSectionVO(companyId, annualId);
            if (companyName && !data.client) {
                data.client = companyName;
            }
            setHeader(data);
            const pages: Record<string, Record<string, string>> = {};
            for (const code of Q_SECTION_PAGES) {
                pages[code] = withHeaderDefaults(toStringRecord(data.pages?.[code]), data, code);
            }
            setPageData(pages);
            setDirty({});
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to load Q Section';
            setError(msg);
            setHeader(null);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, companyName]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        setPageKey(initialPage);
    }, [initialPage]);

    const handleFieldChange = useCallback((pageCode: QSectionPageCode, key: string, value: string) => {
        setPageData((prev) => ({
            ...prev,
            [pageCode]: {...(prev[pageCode] ?? {}), [key]: value},
        }));
        setDirty((prev) => ({...prev, [pageCode]: true}));
    }, []);

    const handleSave = useCallback(async () => {
        if (!dirty[pageKey]) return;
        setSaving(true);
        setError(null);
        try {
            await saveQSectionPage(companyId, annualId, pageKey, pageData[pageKey] ?? {});
            setDirty((prev) => ({...prev, [pageKey]: false}));
        } catch (e: unknown) {
            const msg = e instanceof Error ? e.message : 'Failed to save';
            setError(msg);
        } finally {
            setSaving(false);
        }
    }, [companyId, annualId, dirty, pageKey, pageData]);

    const q0Content = (getContent('Q0') ?? Q0_PAGE) as QProgrammePage;
    const q1Content = (getContent('Q1.x11') ?? Q1_X11_PAGE) as QLeadPage;
    const q12Content = (getContent('Q1.2') ?? Q1_2_PAGE) as QCutoffPage;
    const q2Content = (getContent('Q2.x11') ?? Q2_X11_PAGE) as QLeadPage;

    const tabs = useMemo(() => Q_SECTION_PAGES.map((code) => ({
        key: code,
        label: <PageTabLabel code={code} title={Q_PAGE_TITLES[code]}/>,
        icon: code,
        content: header ? (
            code === 'Q0' ? (
                <G0ProgrammeView
                    header={header}
                    page={q0Content}
                    refCode="Q0"
                    values={pageData.Q0 ?? {}}
                    onChange={(k, v) => handleFieldChange('Q0', k, v)}
                />
            ) : code === 'Q1.2' ? (
                <QCutoffView
                    header={header}
                    page={q12Content}
                    values={pageData['Q1.2'] ?? {}}
                    onChange={(k, v) => handleFieldChange('Q1.2', k, v)}
                />
            ) : (
                <JBreakdownView
                    header={header}
                    page={code === 'Q1.x11' ? q1Content : q2Content}
                    refCode={code}
                    values={pageData[code] ?? {}}
                    onChange={(k, v) => handleFieldChange(code, k, v)}
                />
            )
        ) : null,
    })), [header, pageData, handleFieldChange, q0Content, q1Content, q12Content, q2Content]);

    if (loading) {
        return <Skeleton variant="rectangular" height={480} sx={{m: 2}}/>;
    }

    if (error && !header) {
        return (
            <Box sx={{p: 2}}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column'}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{px: 2, pt: 1, pb: 0.5}}>
                <Typography variant="subtitle1" fontWeight={600}>
                    Q Section — Income & expenditure
                </Typography>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={<SaveIcon/>}
                    disabled={!dirty[pageKey] || saving}
                    onClick={() => void handleSave()}
                >
                    {saving ? 'Saving…' : 'Save'}
                </Button>
            </Stack>
            {error ? (
                <Typography color="error" variant="body2" sx={{px: 2}}>{error}</Typography>
            ) : null}
            <Box sx={{flex: 1, minHeight: 0}}>
                <SplitTabsPanel
                    tabs={tabs}
                    selectedKey={pageKey}
                    onChange={(k) => setPageKey(k as QSectionPageCode)}
                    height="100%"
                    allowMaximize={false}
                    contentWrapperSx={{height: '100%', overflow: 'auto', p: 0}}
                    rightPaneSx={{height: '100%', overflow: 'hidden'}}
                />
            </Box>
        </Box>
    );
}
